import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import {
  CaretRight,
  CaretDown,
  VideoCamera,
  PuzzlePiece,
  Lightning,
  Spinner,
} from "@phosphor-icons/react";
import {
  adminGetModulesByTopic,
  adminGetModuleContentByModuleId,
  adminGetUnitMls,
  adminGetMlParts,
  adminGePartPractices,
} from "../queries";
import { IModule, IAssignedUnit } from "../../../types/generalTypes";
import { IAdminUnitMl, IAdminMlPart, TPracticeLang } from "../adminTypes";
import { IPractice } from "../../../types/generalTypes";
import { TPracticeTypes } from "../../../types/practiceTypes";

type TTopic = "Data Analytics" | "Fullstack Main";
const TOPICS: TTopic[] = ["Data Analytics", "Fullstack Main"];

// ─── helpers ───────────────────────────────────────────────────────────────

const PRACTICE_TYPE_STYLE: Record<TPracticeTypes, string> = {
  TEST: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  CODE: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  REACT: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30",
  PYTHON: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
};

const PRACTICE_TYPE_LABEL: Record<TPracticeTypes, string> = {
  TEST: "Test",
  CODE: "Kod",
  REACT: "React",
  PYTHON: "Python",
};

const DIFFICULTY_STYLE: Record<string, string> = {
  EASY: "text-emerald-400",
  MEDIUM: "text-amber-400",
  HARD: "text-red-400",
};

function ExpandIcon({ open }: { open: boolean }) {
  return open ? (
    <CaretDown size={12} className="text-white/40 shrink-0" />
  ) : (
    <CaretRight size={12} className="text-white/40 shrink-0" />
  );
}

function LoadingDots() {
  return (
    <div className="flex items-center gap-1.5 py-2 px-3 text-white/30 text-xs">
      <Spinner size={12} className="animate-spin" />
      <span>Yükleniyor...</span>
    </div>
  );
}

// ─── Practice Item ──────────────────────────────────────────────────────────

function PracticeItem({ practice }: { practice: IPractice }) {
  const title = practice.description.title;
  const difficulty = practice.difficulty;
  const lang = (practice as { language?: TPracticeLang }).language;

  return (
    <div className="flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
      <span
        className={clsx(
          "mt-px px-1.5 py-0.5 rounded text-[10px] font-bold font-mono shrink-0",
          PRACTICE_TYPE_STYLE[practice.type]
        )}
      >
        {PRACTICE_TYPE_LABEL[practice.type]}
      </span>
      <span className="text-white/75 text-xs flex-1 leading-relaxed">{title}</span>
      <div className="flex items-center gap-2 shrink-0">
        {difficulty && difficulty in DIFFICULTY_STYLE && (
          <span className={clsx("text-[10px] font-semibold", DIFFICULTY_STYLE[difficulty])}>
            {difficulty}
          </span>
        )}
        {lang && (
          <span className="text-white/30 text-[10px] font-mono">{lang}</span>
        )}
      </div>
    </div>
  );
}

// ─── Part Row ───────────────────────────────────────────────────────────────

function PartRow({ part }: { part: IAdminMlPart }) {
  const [open, setOpen] = useState(false);

  const { data: practices, isLoading } = useQuery({
    ...adminGePartPractices({ partId: part.id }),
    enabled: open,
  });

  const practiceCount = practices?.length ?? 0;

  return (
    <div>
      <div
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/[0.04] cursor-pointer transition-colors group"
      >
        <ExpandIcon open={open} />
        <span className="text-white/60 text-xs flex-1">{part.part_title}</span>
        <div className="flex items-center gap-3 text-[10px] text-white/25 opacity-0 group-hover:opacity-100 transition-opacity">
          {part.video_duration && (
            <span className="flex items-center gap-1">
              <VideoCamera size={10} />
              {part.video_duration}
            </span>
          )}
          {part.practice_duration && (
            <span className="flex items-center gap-1">
              <PuzzlePiece size={10} />
              {part.practice_duration}
            </span>
          )}
        </div>
        {open && practiceCount > 0 && (
          <span className="text-[10px] text-white/30 shrink-0">{practiceCount} pratik</span>
        )}
      </div>

      {open && (
        <div className="ml-5 flex flex-col gap-1 mt-1 mb-2">
          {isLoading && <LoadingDots />}
          {!isLoading && practices?.length === 0 && (
            <div className="text-white/20 text-xs px-3 py-1">Pratik yok</div>
          )}
          {practices?.map((p) => <PracticeItem key={p.id} practice={p} />)}
        </div>
      )}
    </div>
  );
}

// ─── ML Row ─────────────────────────────────────────────────────────────────

function MlRow({ ml }: { ml: IAdminUnitMl }) {
  const [open, setOpen] = useState(false);

  const { data: parts, isLoading } = useQuery(
    adminGetMlParts({ mlId: ml.id, enabled: open })
  );

  const partCount = parts?.length ?? 0;

  return (
    <div>
      <div
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] cursor-pointer transition-colors"
      >
        <ExpandIcon open={open} />
        <span className="text-white/80 text-sm flex-1">{ml.ml_title}</span>
        <div className="flex items-center gap-2 shrink-0">
          {ml.is_zoom && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/20 text-violet-300 border border-violet-500/30">
              Zoom
            </span>
          )}
          {open && partCount > 0 && (
            <span className="text-[10px] text-white/30">{partCount} bölüm</span>
          )}
        </div>
      </div>

      {open && (
        <div className="ml-4 flex flex-col gap-0.5 mt-0.5 mb-1">
          {isLoading && <LoadingDots />}
          {!isLoading && parts?.length === 0 && (
            <div className="text-white/20 text-xs px-3 py-1">Bölüm yok</div>
          )}
          {parts
            ?.sort((a, b) => a.order - b.order)
            .map((part) => <PartRow key={part.id} part={part} />)}
        </div>
      )}
    </div>
  );
}

// ─── Unit Row ────────────────────────────────────────────────────────────────

function UnitRow({ unit }: { unit: IAssignedUnit }) {
  const [open, setOpen] = useState(false);

  const { data: mls, isLoading } = useQuery(
    adminGetUnitMls({ unitId: unit.id, enabled: open })
  );

  const mlCount = mls?.length ?? 0;

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden">
      <div
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2.5 px-4 py-2.5 bg-white/[0.04] hover:bg-white/[0.07] cursor-pointer transition-colors"
      >
        <ExpandIcon open={open} />
        <Lightning size={13} className="text-amber-400/60 shrink-0" />
        <span className="text-white/85 text-sm font-medium flex-1">{unit.name}</span>
        {open && mlCount > 0 && (
          <span className="text-[10px] text-white/30 shrink-0">{mlCount} modül</span>
        )}
      </div>

      {open && (
        <div className="px-3 py-2 flex flex-col gap-0.5">
          {isLoading && <LoadingDots />}
          {!isLoading && mls?.length === 0 && (
            <div className="text-white/20 text-xs px-2 py-1">Modül yok</div>
          )}
          {mls?.map((ml) => <MlRow key={ml.id} ml={ml} />)}
        </div>
      )}
    </div>
  );
}

// ─── Module Row ──────────────────────────────────────────────────────────────

function ModuleRow({ module }: { module: IModule }) {
  const [open, setOpen] = useState(false);

  const { data: contents, isLoading } = useQuery(
    adminGetModuleContentByModuleId({ moduleId: module.id, enabled: open })
  );

  const units = (contents?.filter((c) => c.tag === "UNIT") ?? []) as IAssignedUnit[];

  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      {/* Module Header */}
      <div
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-3 px-5 py-3.5 bg-white/[0.06] hover:bg-white/[0.09] cursor-pointer transition-colors"
      >
        <ExpandIcon open={open} />
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm leading-tight">{module.name}</div>
          {module.outer_links && (
            <div className="text-white/30 text-xs mt-0.5 truncate">{module.outer_links}</div>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {open && units.length > 0 && (
            <span className="text-xs text-white/30">{units.length} ders</span>
          )}
          <span className="text-white/20 text-xs font-mono">#{module.order}</span>
        </div>
      </div>

      {/* Module Contents */}
      {open && (
        <div className="px-4 py-3 flex flex-col gap-2">
          {isLoading && <LoadingDots />}
          {!isLoading && units.length === 0 && (
            <div className="text-white/20 text-xs px-2">İçerik yok</div>
          )}
          {units.map((unit) => <UnitRow key={unit.id} unit={unit} />)}
        </div>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function CurriculumMain() {
  const [topic, setTopic] = useState<TTopic>("Data Analytics");

  const { data: modules, isLoading, isError } = useQuery(adminGetModulesByTopic(topic));

  const sorted = [...(modules ?? [])].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-[#0f0f13] text-white">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-bold text-white mb-1">Müfredat</h1>
          <p className="text-white/40 text-sm">Modüller, dersler ve pratikler</p>
        </div>

        {/* Topic Selector */}
        <div className="flex gap-2 mb-6 p-1 bg-white/[0.04] rounded-xl w-fit">
          {TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t)}
              className={clsx(
                "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                topic === t
                  ? "bg-violet-600 text-white"
                  : "text-white/50 hover:text-white/80"
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading && (
          <div className="flex items-center gap-2 text-white/40 text-sm py-8">
            <Spinner size={16} className="animate-spin" />
            <span>Modüller yükleniyor...</span>
          </div>
        )}

        {isError && (
          <div className="text-red-400/80 text-sm py-4 px-4 bg-red-500/10 rounded-xl border border-red-500/20">
            Modüller yüklenirken hata oluştu.
          </div>
        )}

        {!isLoading && !isError && sorted.length === 0 && (
          <div className="text-white/30 text-sm py-8 text-center">
            Bu topic için modül bulunamadı.
          </div>
        )}

        <div className="flex flex-col gap-3">
          {sorted.map((module) => (
            <ModuleRow key={module.id} module={module} />
          ))}
        </div>
      </div>
    </div>
  );
}
